/**
 * Mistake Review Page
 * Intelligent wrong-answer review system
 */
import { useState, useEffect } from 'react';
import { UserProgress } from '../services/storage';
import { MistakeReviewService, initMistakeReviewService, MistakeRecord } from '../services/mistake-review';

interface MistakeReviewPageProps {
  user: UserProgress;
}

export default function MistakeReviewPage({ user }: MistakeReviewPageProps) {
  const [service, setService] = useState<MistakeReviewService | null>(null);
  const [mistakes, setMistakes] = useState<MistakeRecord[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [currentReview, setCurrentReview] = useState<MistakeRecord | null>(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [reviewMode, setReviewMode] = useState<'list' | 'review'>('list');

  useEffect(() => {
    initMistakeReviewService(user.id);
    const mistakeService = new MistakeReviewService(user.id);
    setService(mistakeService);
    loadData(mistakeService);
  }, [user.id]);

  const loadData = (mistakeService: MistakeReviewService) => {
    setMistakes(mistakeService.getMistakesForReview(20));
    setStats(mistakeService.getStatistics());
  };

  const handleStartReview = () => {
    const toReview = service?.getMistakesForReview(10);
    if (toReview && toReview.length > 0) {
      setCurrentReview(toReview[0]);
      setReviewMode('review');
      setShowAnswer(false);
    } else {
      alert('🎉 太棒了！没有需要复习的错题！');
    }
  };

  const handleShowAnswer = () => {
    setShowAnswer(true);
  };

  const handleMarkCorrect = () => {
    if (!currentReview || !service) return;

    service.markReviewed(currentReview.id, true);
    const remaining = mistakes.filter(m => m.id !== currentReview.id);

    if (remaining.length > 0) {
      setCurrentReview(remaining[0]);
      setShowAnswer(false);
      setMistakes(service.getMistakesForReview(20));
    } else {
      setReviewMode('list');
      loadData(service);
      alert('🎉 复习完成！太棒了！');
    }
  };

  const handleMarkWrong = () => {
    if (!currentReview || !service) return;

    service.markReviewed(currentReview.id, false);
    const remaining = mistakes.filter(m => m.id !== currentReview.id);

    if (remaining.length > 0) {
      setCurrentReview(remaining[0]);
      setShowAnswer(false);
      setMistakes(service.getMistakesForReview(20));
    } else {
      setReviewMode('list');
      loadData(service);
      alert('继续加油！下次一定能记住 💪');
    }
  };

  if (!service) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin text-4xl">⏳</div>
        <p className="text-gray-500 mt-4">加载中...</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-400 to-pink-500 rounded-3xl p-6 text-white mb-6 shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">错题本 📝</h1>
            <p className="text-white/80 text-sm">查漏补缺，温故知新</p>
          </div>
          <div className="text-5xl animate-float">📚</div>
        </div>
      </div>

      {reviewMode === 'list' ? (
        <>
          {/* Statistics */}
          <div className="bg-white rounded-3xl p-6 shadow-soft mb-6">
            <h3 className="font-bold text-gray-700 mb-4">错题统计</h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="bg-red-50 rounded-xl p-3">
                <p className="text-2xl font-bold text-red-600">{stats?.total || 0}</p>
                <p className="text-xs text-gray-500">待复习</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-3">
                <p className="text-2xl font-bold text-blue-600">{stats?.reviewed || 0}</p>
                <p className="text-xs text-gray-500">已复习</p>
              </div>
              <div className="bg-green-50 rounded-xl p-3">
                <p className="text-2xl font-bold text-green-600">{stats?.mastered || 0}</p>
                <p className="text-xs text-gray-500">已掌握</p>
              </div>
            </div>

            {/* Most common mistakes */}
            {stats?.mostCommon && stats.mostCommon.length > 0 && (
              <div className="mt-4">
                <p className="text-sm text-gray-500 mb-2">最常出错：</p>
                <div className="flex flex-wrap gap-2">
                  {stats.mostCommon.slice(0, 5).map(m => (
                    <span
                      key={m.id}
                      className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium"
                    >
                      {m.content} ({m.wrongCount}次)
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Start review button */}
          <button
            onClick={handleStartReview}
            className="w-full bg-gradient-to-r from-red-400 to-pink-500 text-white py-4 rounded-2xl font-bold text-lg hover:opacity-90 transition-all mb-6"
          >
            🎯 开始复习 ({mistakes.length}题)
          </button>

          {/* Mistake list */}
          {mistakes.length > 0 && (
            <div className="bg-white rounded-3xl p-6 shadow-soft">
              <h3 className="font-bold text-gray-700 mb-4">错题列表</h3>
              <div className="space-y-3">
                {mistakes.slice(0, 10).map(mistake => (
                  <div
                    key={mistake.id}
                    className="bg-gray-50 rounded-xl p-3 border-l-4 border-red-400"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-bold text-gray-800">{mistake.content}</p>
                        <p className="text-sm text-red-600 mt-1">
                          你的答案：{mistake.userAnswer}
                        </p>
                        <p className="text-sm text-green-600">
                          正确答案：{mistake.correctAnswer}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                          {mistake.wrongCount}次
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty state */}
          {mistakes.length === 0 && (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🎉</div>
              <p className="text-lg font-bold text-gray-700">太棒了！</p>
              <p className="text-gray-500">没有错题记录，继续保持！</p>
            </div>
          )}
        </>
      ) : (
        /* Review mode */
        <div className="bg-white rounded-3xl p-6 shadow-soft">
          {/* Progress */}
          <div className="mb-6 text-center">
            <p className="text-sm text-gray-500">
              第 {mistakes.findIndex(m => m.id === currentReview?.id) + 1} / {mistakes.length} 题
            </p>
          </div>

          {/* Question */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 mb-6">
            <p className="text-gray-600 mb-2">题目：</p>
            <p className="text-xl font-bold text-gray-800">{currentReview?.content}</p>
          </div>

          {/* Your previous answer */}
          <div className="bg-red-50 rounded-xl p-4 mb-6">
            <p className="text-sm text-gray-600 mb-1">你之前的答案：</p>
            <p className="text-lg font-bold text-red-700">{currentReview?.userAnswer}</p>
          </div>

          {/* Show answer button */}
          {!showAnswer && (
            <button
              onClick={handleShowAnswer}
              className="w-full bg-blue-500 text-white py-3 rounded-xl font-bold mb-6"
            >
              👀 查看正确答案
            </button>
          )}

          {/* Correct answer */}
          {showAnswer && (
            <div className="bg-green-50 rounded-xl p-4 mb-6 animate-bounce-in">
              <p className="text-sm text-gray-600 mb-1">正确答案：</p>
              <p className="text-lg font-bold text-green-700">{currentReview?.correctAnswer}</p>
            </div>
          )}

          {/* Action buttons */}
          {showAnswer && (
            <div className="flex gap-3">
              <button
                onClick={handleMarkCorrect}
                className="flex-1 bg-green-500 text-white py-3 rounded-xl font-bold hover:bg-green-600"
              >
                ✅ 我记住了
              </button>
              <button
                onClick={handleMarkWrong}
                className="flex-1 bg-red-500 text-white py-3 rounded-xl font-bold hover:bg-red-600"
              >
                ❌ 还是错了
              </button>
            </div>
          )}

          {/* Exit review */}
          <button
            onClick={() => setReviewMode('list')}
            className="w-full mt-6 text-gray-500 hover:text-gray-700"
          >
            ← 退出复习
          </button>
        </div>
      )}

      {/* Tips */}
      <div className="mt-6 bg-gray-50 rounded-xl p-4 text-center">
        <p className="text-sm text-gray-500">
          💡 提示：连续答对3次后，错题会自动移除
        </p>
      </div>
    </div>
  );
}